export const SOCKET_STATE = Object.freeze({
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  DISCONNECTING: 'DISCONNECTING',
  DISCONNECTED: 'DISCONNECTED',
  USER_DISCONNECTED: 'USER_DISCONNECTED',
  RECONNECTING: 'RECONNECTING'
})

export const SOCKET_EVENT = Object.freeze({
  JUST_DIED: 'JUST_DIED',
  ON_MESSAGE_JSON: 'ON_MESSAGE_JSON',
  ON_MESSAGE: 'ON_MESSAGE'
})

export class WebSocketClient {
  constructor(
    url,
    {
      withDebug = false,
      withHealthCheck = false,
      withPingPongProtocol = false,
      withReconnection = false
    } = {}
  ) {
    this.url = url
    this.socket = null
    this.state = SOCKET_STATE.DISCONNECTED
    this.listeners = {}

    this.isDebugEnabled = withDebug
    this.isHealthCheckEnabled = withHealthCheck
    this.healthIntervalId = null
    this.healthCheckInterval = 30_000
    this.lifetimeDuration = this.healthCheckInterval * 1.2
    this.lastAliveAt = 0
    this.isPingPongProtocolEnabled = withPingPongProtocol
    this.isReconnectionEnabled = withReconnection
  }

  #setState(state) {
    this.state = state
    this.#log(`State changed to ${state}`)
    this.#emit(state)
  }

  #log(msg, { isError = false } = {}) {
    if (!this.isDebugEnabled) return
    isError ? console.error(`[WSS] ${msg}`) : console.debug(`[WSS] ${msg}`)
  }

  #on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = new Set()
    }

    this.listeners[event].add(callback)
    return () => this.listeners[event].delete(callback)
  }

  #emit(event, payload = null) {
    if (!this.listeners[event]) return
    this.listeners[event].forEach((f) => f(payload))
  }

  #startHealthCheck() {
    if (this.healthIntervalId != null) return
    if (!this.isHealthCheckEnabled) return
    this.#log('Health check started')

    this.#heal()
    this.healthIntervalId = setInterval(() => {
      if (this.state !== SOCKET_STATE.CONNECTED) return

      const now = Date.now()
      if (now - this.lastAliveAt > this.lifetimeDuration) {
        this.#emit(SOCKET_EVENT.JUST_DIED, { reason: 'Connection timed out' })
        this.#disconnect()

        if (this.isReconnectionEnabled) {
          this.#reconnect()
        }
      }

      if (this.isPingPongProtocolEnabled) {
        try {
          this.sendMessage({ action: 'ping' })
          this.#log('Health check: ping action sent')
        } catch (e) {
          this.#log(`Service unhealthy: ${e}`, { isError: true })
          this.#emit(SOCKET_EVENT.JUST_DIED, { reason: e })
        }
      }
    }, this.healthCheckInterval)
  }

  #stopHealthCheck() {
    if (!this.healthIntervalId == null) return
    if (!this.isHealthCheckEnabled) return

    clearInterval(this.healthIntervalId)
    this.healthIntervalId = null

    this.#log('Health check stopped')
  }

  #heal() {
    this.lastAliveAt = Date.now()
  }

  #reconnect() {
    if (this.state === SOCKET_STATE.RECONNECTING) return
    if (this.state === SOCKET_STATE.USER_DISCONNECTED) return

    if (this.socket?.readyState !== WebSocket.CLOSED) {
      this.#disconnect()
      return
    }

    this.#setState(SOCKET_STATE.RECONNECTING)
    this.connect()
  }

  connect() {
    if (this.state === SOCKET_STATE.CONNECTING || this.state === SOCKET_STATE.CONNECTED) return

    this.#setState(SOCKET_STATE.CONNECTING)
    this.socket = new WebSocket(this.url)

    this.socket.onopen = () => {
      this.#setState(SOCKET_STATE.CONNECTED)
      this.#startHealthCheck()
    }

    this.socket.onmessage = (event) => {
      this.#onMessage(event)
    }

    this.socket.onerror = (error) => {
      this.#log(`Error ${error}`, { isError: true })
    }

    this.socket.onclose = () => {
      this.#stopHealthCheck()
      if (this.state !== SOCKET_STATE.USER_DISCONNECTED) {
        this.#setState(SOCKET_STATE.DISCONNECTED)
        this.#reconnect()
      }
    }
  }

  #disconnect() {
    if (
      ![
        SOCKET_STATE.DISCONNECTED,
        SOCKET_STATE.USER_DISCONNECTED,
        SOCKET_STATE.DISCONNECTING
      ].includes(this.state)
    ) {
      this.#setState(SOCKET_STATE.DISCONNECTING)
    }

    this.#stopHealthCheck()
    this.socket?.close()
  }

  disconnect() {
    if (this.isReconnectionEnabled) {
      this.#setState(SOCKET_STATE.USER_DISCONNECTED)
    }

    this.#disconnect()
  }

  #handleMessagePing(message) {
    if (!this.isHealthCheckEnabled) return false
    if (!this.isPingPongProtocolEnabled) return false

    const hasReceivedPong = message?.action === 'ping' && message?.status === 'pong'
    if (hasReceivedPong) {
      this.#log('Health check: pong received')
      this.#heal()
    }
    return hasReceivedPong
  }

  #handleMessageKeepAlive(message) {
    if (!this.isHealthCheckEnabled) return false

    const hasReceivedKeepAlive = message?.status === 'keep_alive'
    if (hasReceivedKeepAlive) {
      this.#log('Health check: keep_alive received')
      this.#heal()
    }
    return hasReceivedKeepAlive
  }

  #onMessage(message) {
    this.#log(`Received message: ${message}`)
    try {
      if (message.data == null) return

      const response = JSON.parse(message.data)
      if (this.#handleMessagePing(response)) return
      if (this.#handleMessageKeepAlive(response)) return

      this.#emit('ON_MESSAGE_JSON', response)
      return
    } catch (exception) {
      if (exception instanceof SyntaxError) {
        this.#emit('ON_MESSAGE', message)
      } else {
        this.#log(`Error parsing message: ${exception}`, { isError: true })
      }
    }

    this.#emit('ON_MESSAGE', message)
  }

  on(event, callback) {
    return this.#on(event, callback)
  }

  off(event, callback) {
    this.listeners[event]?.delete(callback)
  }

  sendMessage(message) {
    this.socket.send(JSON.stringify(message))
  }
}
